/**
 * Firestore REST API service
 * Uses plain fetch() instead of the Firestore SDK WebChannel,
 * which is blocked on some networks/firewalls.
 * 
 * For authenticated writes, we get the Firebase Auth ID token and pass it
 * as a Bearer token. Reads use the API key (public rules allow reads).
 */
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs } from 'firebase/firestore';
import app, { firebaseConfig } from 'services/firebase';

const PROJECT = firebaseConfig.projectId;
const API_KEY = firebaseConfig.apiKey;
const BASE    = `https://firestore.googleapis.com/v1/projects/${PROJECT}/databases/(default)/documents`;

const auth = getAuth(app);

// ─── Auth token ──────────────────────────────────────────────────────────────
const getToken = async () => {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
};

const authHeaders = async () => {
    const token = await getToken();
    return token
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        : { 'Content-Type': 'application/json' };
};

// ─── Firestore value converters ───────────────────────────────────────────────
const buildValue = (v) => {
    if (v === null) return { nullValue: null };
    if (v === undefined) return null;
    if (Array.isArray(v)) {
        return { arrayValue: { values: v.map(item => buildValue(item) || { nullValue: null }) } };
    }
    if (typeof v === 'object') {
        // map / object
        const fields = {};
        for (const [k, val] of Object.entries(v)) {
            const built = buildValue(val);
            if (built !== null) fields[k] = built;
        }
        return { mapValue: { fields } };
    }
    if (typeof v === 'number') return { doubleValue: v };
    if (typeof v === 'boolean') return { booleanValue: v };
    return { stringValue: String(v) };
};

const toFirestore = (obj) => {
    const fields = {};
    for (const [k, v] of Object.entries(obj)) {
        const value = buildValue(v);
        if (value !== null && value !== undefined) fields[k] = value;
    }
    return { fields };
};

const parseValue = (v) => {
    if (v === undefined || v === null) return null;
    if (v.stringValue !== undefined) return v.stringValue;
    if (v.doubleValue !== undefined) return v.doubleValue;
    if (v.integerValue !== undefined) return Number(v.integerValue);
    if (v.booleanValue !== undefined) return v.booleanValue;
    if (v.timestampValue !== undefined) return v.timestampValue;
    if (v.arrayValue) return (v.arrayValue.values || []).map(parseValue);
    if (v.mapValue) {
        const obj = {};
        for (const [kk, vv] of Object.entries(v.mapValue.fields || {})) {
            obj[kk] = parseValue(vv);
        }
        return obj;
    }
    return null;
};

const fromFirestore = (doc) => {
    const id = doc.name.split('/').pop();
    const out = { firestoreId: id };
    for (const [k, v] of Object.entries(doc.fields || {})) {
        out[k] = parseValue(v);
    }
    return out;
};

// ─── Generic REST helpers ─────────────────────────────────────────────────────
const restGet = async (col) => {
    const res = await fetch(`${BASE}/${col}?key=${API_KEY}`);
    if (!res.ok) throw new Error(`GET ${col} failed: ${res.status} ${await res.text()}`);
    const json = await res.json();
    return (json.documents || []).map(fromFirestore);
};

const restAdd = async (col, data) => {
    const headers = await authHeaders();
    const res = await fetch(`${BASE}/${col}?key=${API_KEY}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(toFirestore(data)),
    });
    if (!res.ok) throw new Error(`ADD ${col} failed: ${res.status} ${await res.text()}`);
    const doc = await res.json();
    return doc.name.split('/').pop();
};

const restUpdate = async (col, firestoreId, data) => {
    const headers = await authHeaders();
    // Firestore REST API requires updateMask.fieldPaths repeated for each field
    const fieldKeys = Object.keys(data || {});
    const fieldParams = fieldKeys.map(k => `&updateMask.fieldPaths=${encodeURIComponent(k)}`).join('');
    const url = `${BASE}/${col}/${firestoreId}?key=${API_KEY}${fieldParams}`;
    const res = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(toFirestore(data)),
    });
    if (!res.ok) throw new Error(`UPDATE ${col}/${firestoreId} failed: ${res.status} ${await res.text()}`);
};

const restDelete = async (col, firestoreId) => {
    const headers = await authHeaders();
    const res = await fetch(`${BASE}/${col}/${firestoreId}?key=${API_KEY}`, {
        method: 'DELETE',
        headers,
    });
    if (!res.ok) throw new Error(`DELETE ${col}/${firestoreId} failed: ${res.status} ${await res.text()}`);
};

// ─── Set (upsert by known ID) ─────────────────────────────────────────────────
const restSet = async (col, firestoreId, data) => {
    const headers = await authHeaders();
    const res = await fetch(`${BASE}/${col}/${firestoreId}?key=${API_KEY}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(toFirestore(data)),
    });
    if (!res.ok) throw new Error(`SET ${col}/${firestoreId} failed: ${res.status} ${await res.text()}`);
};

// ─── Collection names ─────────────────────────────────────────────────────────
export const FOODS_COL              = 'stocks_foods';
export const MARKETS_COL            = 'stocks_markets';
export const FEATURED_COL           = 'stocks_featured';
export const CATEGORY_COL           = 'stocks_category';
export const TRANSACTIONS_COL       = 'transactions';
export const USERS_COL              = 'users';
export const CO_BUY_GROUPS_COL      = 'co_buy_groups';
export const CO_BUY_INTERESTS_COL   = 'co_buy_interests';
export const PARTNERS_COL           = 'partners';
export const NEWSLETTER_COL         = 'newsletter_subscriptions';
export const CONTACT_MESSAGES_COL   = 'contact_messages';
export const BLOG_POSTS_COL         = 'blog_posts';
export const NEWS_COL               = 'news_articles';
export const REVIEWS_COL            = 'reviews';
export const APP_SETTINGS_COL       = 'app_settings';
export const SERVED_STATES_COL      = 'served_states';

export const CLEARABLE_COLLECTIONS = [
    { key: 'foods', label: 'Foods Catalog', col: FOODS_COL, group: 'Catalog & Inventory', description: 'All food products, pricing, units & images' },
    { key: 'categories', label: 'Categories', col: CATEGORY_COL, group: 'Catalog & Inventory', description: 'Product categories & classifications' },
    { key: 'locations', label: 'Locations / Served Areas', col: SERVED_STATES_COL, group: 'Catalog & Inventory', description: 'Delivery states and neighborhood service areas' },
    { key: 'markets', label: 'Markets', col: MARKETS_COL, group: 'Catalog & Inventory', description: 'Market entries and affiliated stores' },
    { key: 'featured', label: 'Featured Items', col: FEATURED_COL, group: 'Catalog & Inventory', description: 'Featured foods highlighted on homepage' },
    { key: 'orders', label: 'Orders & Transactions', col: TRANSACTIONS_COL, group: 'Orders & Operations', description: 'Customer orders, payments, references & tracking' },
    { key: 'co_buy_groups', label: 'Co-Buying Groups', col: CO_BUY_GROUPS_COL, group: 'Orders & Operations', description: 'Co-op pooling groups and member contributions' },
    { key: 'co_buy_interests', label: 'Co-Buying Interests', col: CO_BUY_INTERESTS_COL, group: 'Orders & Operations', description: 'Submitted customer interests in co-buying' },
    { key: 'partners', label: 'Partners / Vendors', col: PARTNERS_COL, group: 'Orders & Operations', description: 'Partner vendor applications and profiles' },
    { key: 'reviews', label: 'Customer Reviews', col: REVIEWS_COL, group: 'Content & Inquiries', description: 'Product reviews and customer ratings' },
    { key: 'news', label: 'News Articles', col: NEWS_COL, group: 'Content & Inquiries', description: 'Published news items' },
    { key: 'blog', label: 'Blog Posts', col: BLOG_POSTS_COL, group: 'Content & Inquiries', description: 'Blog articles and resources' },
    { key: 'messages', label: 'Contact Messages', col: CONTACT_MESSAGES_COL, group: 'Content & Inquiries', description: 'Inquiries sent via Contact Us form' },
    { key: 'newsletter', label: 'Newsletter Subscriptions', col: NEWSLETTER_COL, group: 'Content & Inquiries', description: 'Subscriber email list' },
];

// ─── Batch Deletion & Collection Clearing ────────────────────────────────────
export const fetchAllDocumentIds = async (col) => {
    let docIds = [];
    let pageToken = '';
    do {
        const tokenParam = pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '';
        const res = await fetch(`${BASE}/${col}?key=${API_KEY}&pageSize=300${tokenParam}`);
        if (!res.ok) {
            if (res.status === 404) return [];
            throw new Error(`GET ${col} failed: ${res.status} ${await res.text()}`);
        }
        const json = await res.json();
        const docs = json.documents || [];
        for (const doc of docs) {
            const id = doc.name.split('/').pop();
            if (id) docIds.push(id);
        }
        pageToken = json.nextPageToken;
    } while (pageToken);
    return docIds;
};

export const batchDeleteDocuments = async (col, docIds) => {
    if (!docIds || docIds.length === 0) return 0;
    const headers = await authHeaders();
    const CHUNK_SIZE = 200;
    for (let i = 0; i < docIds.length; i += CHUNK_SIZE) {
        const chunk = docIds.slice(i, i + CHUNK_SIZE);
        const writes = chunk.map(id => ({
            delete: `projects/${PROJECT}/databases/(default)/documents/${col}/${id}`
        }));
        try {
            const commitUrl = `https://firestore.googleapis.com/v1/projects/${PROJECT}/databases/(default)/documents:commit?key=${API_KEY}`;
            const res = await fetch(commitUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify({ writes })
            });
            if (!res.ok) {
                console.warn(`Commit batch failed for ${col} (${res.status}), falling back to parallel restDelete`);
                await Promise.all(chunk.map(id => restDelete(col, id)));
            }
        } catch (e) {
            console.warn(`Error during commit for ${col}, falling back to individual deletes:`, e);
            await Promise.all(chunk.map(id => restDelete(col, id)));
        }
    }
    return docIds.length;
};

export const clearCollection = async (col) => {
    const docIds = await fetchAllDocumentIds(col);
    if (docIds.length === 0) return 0;
    return await batchDeleteDocuments(col, docIds);
};

export const clearMultipleCollections = async (collectionKeys = [], onProgress) => {
    const results = {};
    for (const key of collectionKeys) {
        const item = CLEARABLE_COLLECTIONS.find(c => c.key === key || c.col === key);
        if (!item) continue;
        if (onProgress) onProgress({ key: item.key, label: item.label, status: 'in-progress' });
        try {
            const count = await clearCollection(item.col);
            results[item.key] = { success: true, count };
            if (onProgress) onProgress({ key: item.key, label: item.label, status: 'done', count });
        } catch (err) {
            console.error(`Failed to clear collection ${item.col}:`, err);
            results[item.key] = { success: false, error: err.message };
            if (onProgress) onProgress({ key: item.key, label: item.label, status: 'error', error: err.message });
        }
    }
    return results;
};

// ─── Foods ────────────────────────────────────────────────────────────────────
export const fetchFoods    = ()                    => restGet(FOODS_COL);
export const addFood       = (data)                => restAdd(FOODS_COL, data);
export const updateFood    = (id, data)            => restUpdate(FOODS_COL, id, data);
export const deleteFood    = (id)                  => restDelete(FOODS_COL, id);

// ─── Markets ──────────────────────────────────────────────────────────────────
export const fetchMarkets  = ()                    => restGet(MARKETS_COL);
export const addMarket     = (data)                => restAdd(MARKETS_COL, data);
export const updateMarket  = (id, data)            => restUpdate(MARKETS_COL, id, data);
export const deleteMarket  = (id)                  => restDelete(MARKETS_COL, id);

// ─── Featured ─────────────────────────────────────────────────────────────────
export const fetchFeatured = ()                    => restGet(FEATURED_COL);
export const setFeatured   = (id, data)            => restSet(FEATURED_COL, id, data);
export const deleteFeatured= (id)                  => restDelete(FEATURED_COL, id);

// ─── Category ─────────────────────────────────────────────────────────────────
export const fetchCategories = ()                  => restGet(CATEGORY_COL);
export const addCategory     = (data)              => restAdd(CATEGORY_COL, data);
export const updateCategory  = (id, data)          => restUpdate(CATEGORY_COL, id, data);
export const deleteCategory  = (id)                => restDelete(CATEGORY_COL, id);

// ─── Transactions ─────────────────────────────────────────────────────────────
export const fetchTransactions = ()                => restGet(TRANSACTIONS_COL);
export const addTransaction    = (data)            => restAdd(TRANSACTIONS_COL, data);
export const updateTransaction = (id, data)        => restUpdate(TRANSACTIONS_COL, id, data);
export const deleteTransaction = (id)              => restDelete(TRANSACTIONS_COL, id);

// ─── Users ─────────────────────────────────────────────────────────────────────
export const fetchUsers = ()                       => restGet(USERS_COL);
export const fetchUserByEmail = async (email) => {
    const headers = await authHeaders();
    const structuredQuery = {
        from: [{ collectionId: USERS_COL }],
        where: {
            fieldFilter: {
                field: { fieldPath: 'Email' },
                op: 'EQUAL',
                value: { stringValue: email }
            }
        },
        limit: 1
    };
    const url = `https://firestore.googleapis.com/v1/projects/${PROJECT}/databases/(default)/documents:runQuery`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ structuredQuery })
    });
    if (!res.ok) throw new Error(`Query user failed: ${res.status}`);
    const results = await res.json();
    const match = results.find(r => r.document);
    return match ? fromFirestore(match.document) : null;
};
export const updateUser = (id, data)               => restUpdate(USERS_COL, id, data);
export const deleteUser = (id)                     => restDelete(USERS_COL, id);

// ─── Co-Buying Groups ─────────────────────────────────────────────────────────
export const fetchCoBuyGroups = ()                  => restGet(CO_BUY_GROUPS_COL);
export const createCoBuyGroup = (data)              => restAdd(CO_BUY_GROUPS_COL, data);
export const updateCoBuyGroup = (id, data)          => restUpdate(CO_BUY_GROUPS_COL, id, data);
export const deleteCoBuyGroup = (id)                => restDelete(CO_BUY_GROUPS_COL, id);

// ─── Co-Buying Interests ─────────────────────────────────────────────────────────
export const fetchCoBuyInterests = ()               => restGet(CO_BUY_INTERESTS_COL);
export const createCoBuyInterest = (data)           => restAdd(CO_BUY_INTERESTS_COL, data);
export const updateCoBuyInterest = (id, data)       => restUpdate(CO_BUY_INTERESTS_COL, id, data);
export const deleteCoBuyInterest = (id)             => restDelete(CO_BUY_INTERESTS_COL, id);

// ─── Advanced query for transactions by userId (with filtering)
export const fetchUserTransactions = async (userId, options = {}) => {
    const headers = await authHeaders();
    
    // Build structured query
    const structuredQuery = {
        from: [{ collectionId: TRANSACTIONS_COL }],
        where: {
            fieldFilter: {
                field: { fieldPath: 'userId' },
                op: 'EQUAL',
                value: { stringValue: userId }
            }
        },
        orderBy: [
            {
                field: { fieldPath: 'timestamp' },
                direction: 'DESCENDING'
            }
        ]
    };

    // Add limit if specified
    if (options.limit) {
        structuredQuery.limit = options.limit;
    }

    // Add date filter if specified
    if (options.startDate) {
        structuredQuery.where = {
            compositeFilter: {
                op: 'AND',
                filters: [
                    {
                        fieldFilter: {
                            field: { fieldPath: 'userId' },
                            op: 'EQUAL',
                            value: { stringValue: userId }
                        }
                    },
                    {
                        fieldFilter: {
                            field: { fieldPath: 'timestamp' },
                            op: 'GREATER_THAN_OR_EQUAL',
                            value: { timestampValue: options.startDate.toISOString() }
                        }
                    }
                ]
            }
        };
    }

    const url = `https://firestore.googleapis.com/v1/projects/${PROJECT}/databases/(default)/documents:runQuery`;
    
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ structuredQuery })
    });

    console.log('Query transactions response:', res)

    if (!res.ok) {
        throw new Error(`Query transactions failed: ${res.status} ${await res.text()}`);
    }

    const results = await res.json();
    console.log('Query transactions results:', results)
    // Filter out empty results and parse documents
    return results
        .filter(r => r.document)
        .map(r => fromFirestore(r.document));
};

// ─── Partners ─────────────────────────────────────────────────────────────────
export const fetchPartners  = ()           => restGet(PARTNERS_COL);
export const addPartner     = (data)       => restAdd(PARTNERS_COL, data);
export const updatePartner  = (id, data)   => restUpdate(PARTNERS_COL, id, data);
export const deletePartner  = (id)         => restDelete(PARTNERS_COL, id);

// ─── Newsletter subscriptions ───────────────────────────────────────────────
export const fetchNewsletterSubscriptions = () => restGet(NEWSLETTER_COL);
export const addNewsletterSubscription = (data) => restAdd(NEWSLETTER_COL, data);
export const deleteNewsletterSubscription = (id) => restDelete(NEWSLETTER_COL, id);

// ─── Contact messages ───────────────────────────────────────────────────────
export const fetchContactMessages = () => restGet(CONTACT_MESSAGES_COL);
export const addContactMessage = (data) => restAdd(CONTACT_MESSAGES_COL, data);
export const deleteContactMessage = (id) => restDelete(CONTACT_MESSAGES_COL, id);

// ─── Blog posts ─────────────────────────────────────────────────────────────
export const fetchBlogPosts = () => restGet(BLOG_POSTS_COL);
export const addBlogPost = (data) => restAdd(BLOG_POSTS_COL, data);
export const updateBlogPost = (id, data) => restUpdate(BLOG_POSTS_COL, id, data);
export const deleteBlogPost = (id) => restDelete(BLOG_POSTS_COL, id);

// ─── News articles ────────────────────────────────────────────────────────────
export const fetchNews = () => restGet(NEWS_COL);
export const addNews = (data) => restAdd(NEWS_COL, data);
export const updateNews = (id, data) => restUpdate(NEWS_COL, id, data);
export const deleteNews = (id) => restDelete(NEWS_COL, id);

// ─── Users (write) ────────────────────────────────────────────────────────────
export const addUser = (data) => restAdd(USERS_COL, data);

// ─── Reviews ────────────────────────────────────────────────────────────────
export const fetchReviews = () => restGet(REVIEWS_COL);
export const addReview = (data) => restAdd(REVIEWS_COL, data);
export const updateReview = (id, data) => restUpdate(REVIEWS_COL, id, data);
export const deleteReview = (id) => restDelete(REVIEWS_COL, id);

// ─── App Settings ─────────────────────────────────────────────────────────────
export const fetchAppSettings = () => restGet(APP_SETTINGS_COL);
export const updateAppSetting = (id, data) => restUpdate(APP_SETTINGS_COL, id, data);
export const addAppSetting = (data) => restAdd(APP_SETTINGS_COL, data);

// ─── Served States & Neighborhoods ────────────────────────────────────────────
// Admin-managed list of states the business serves, each with the neighborhood
// areas covered within that state. Document shape: { name, neighborhoods: [] }.
export const fetchServedStates = ()          => restGet(SERVED_STATES_COL);
export const addServedState = (data)         => restAdd(SERVED_STATES_COL, data);
export const updateServedState = (id, data)  => restUpdate(SERVED_STATES_COL, id, data);
export const deleteServedState = (id)        => restDelete(SERVED_STATES_COL, id);

// ─── Legacy exports (used by Signup.js) ──────────────────────────────────────
const db = getFirestore(app);
export const HandleAddDoc = { addDoc, collection, db };
export const HandleGetDoc = { getDocs, collection, db };